import { Component, Input, Output, EventEmitter, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-image-modal',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (show) {
      <div
        class="fixed inset-0 z-[200] flex items-center justify-center bg-bg-base/90 backdrop-blur-xl p-3 sm:p-6 select-none animate-fadeIn overflow-hidden"
        (click)="onClose()"
        tabindex="0"
      >
        <div class="relative w-full max-w-5xl max-h-full flex items-center justify-center" (click)="$event.stopPropagation()">
          <img
            [src]="images[currentIndex]"
            [alt]="'Gallery showcase view ' + (currentIndex + 1)"
            class="max-w-full max-h-[78vh] sm:max-h-[85vh] md:max-h-[88vh] object-contain rounded-2xl sm:rounded-3xl shadow-elevated border border-border transition-all duration-300 transform scale-100"
            (error)="handleImageError($event)"
          />

          <div class="absolute top-2 sm:top-[-50px] inset-x-0 flex items-center justify-end sm:justify-between px-2 sm:px-1">
            <div class="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 bg-surface-elevated border border-border rounded-xl text-text-muted text-[10px] font-black uppercase tracking-widest font-mono">
              <i class="fa-solid fa-keyboard text-text-muted/50 text-[11px]"></i>
              <span>Use <span>←</span> <span>→</span> Navigation Keys</span>
            </div>

            <div class="sm:ml-auto"></div>

            <button type="button" (click)="onClose()" class="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-surface-elevated/90 hover:bg-danger border border-border text-text-primary rounded-xl transition-all duration-200 cursor-pointer shadow-lg active:scale-90">
              <i class="fa-solid fa-xmark text-xs sm:text-sm"></i>
            </button>
          </div>

          @if (images.length > 1) {
            <button type="button" (click)="onPrevious()" class="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-14 sm:h-14 flex items-center justify-center bg-surface/80 hover:bg-surface-hover backdrop-blur-xl text-text-secondary hover:text-text-primary border border-border hover:border-border-strong rounded-2xl sm:rounded-3xl transition-all duration-200 shadow-elevated group cursor-pointer active:scale-90">
              <i class="fa-solid fa-chevron-left text-xs sm:text-base group-hover:-translate-x-0.5 transition-transform"></i>
            </button>

            <button type="button" (click)="onNext()" class="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-14 sm:h-14 flex items-center justify-center bg-surface/80 hover:bg-surface-hover backdrop-blur-xl text-text-secondary hover:text-text-primary border border-border hover:border-border-strong rounded-2xl sm:rounded-3xl transition-all duration-200 shadow-elevated group cursor-pointer active:scale-90">
              <i class="fa-solid fa-chevron-right text-xs sm:text-base group-hover:translate-x-0.5 transition-transform"></i>
            </button>
          }

          <div class="absolute bottom-2 sm:bottom-[-50px] left-1/2 -translate-x-1/2 px-4 py-1.5 bg-surface-elevated/90 border border-border text-text-primary text-[11px] font-black uppercase tracking-widest font-mono rounded-xl shadow-lg flex items-center gap-2">
            <span class="text-primary">{{ currentIndex + 1 }}</span>
            <span class="text-text-muted">/</span>
            <span class="text-text-secondary">{{ images.length }}</span>
          </div>
        </div>
      </div>
    }
  `,
})
export class ImageModalComponent {
  private readonly fallbackImage =
    'https://images.unsplash.com/photo-1560185007-5f0bb1866cab?w=1200&h=800&fit=crop';

  @Input() show: boolean = false;
  @Input() images: string[] = [];
  @Input() currentIndex: number = 0;
  @Output() close = new EventEmitter<void>();
  @Output() indexChange = new EventEmitter<number>();

  handleImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img && img.src !== this.fallbackImage) {
      img.src = this.fallbackImage;
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey() {
    if (this.show) {
      this.onClose();
    }
  }

  @HostListener('document:keydown.arrowLeft')
  onLeftKey() {
    if (this.show) {
      this.onPrevious();
    }
  }

  @HostListener('document:keydown.arrowRight')
  onRightKey() {
    if (this.show) {
      this.onNext();
    }
  }

  onClose() {
    this.close.emit();
  }

  onPrevious() {
    if (this.images.length > 1) {
      const newIndex = this.currentIndex > 0 ? this.currentIndex - 1 : this.images.length - 1;
      this.indexChange.emit(newIndex);
    }
  }

  onNext() {
    if (this.images.length > 1) {
      const newIndex = this.currentIndex < this.images.length - 1 ? this.currentIndex + 1 : 0;
      this.indexChange.emit(newIndex);
    }
  }
}



